import { useRef, useState } from "react";
import Modal from "react-modal";
import { useDispatch } from "react-redux";

import {
  AnnotationType,
  AreaCountAnnotation,
  RegionOfInterestAnnotation,
  TextAnnotation,
} from "custom-models/ViewerTypes";

import {
  commitEditedAnnotation,
  setEditedAnnotation,
} from "shared/actions/slideDetailActions";
import {
  LESION_LINE_NAMES,
  LESION_LINE_TYPE_HIERARCHY,
  LESION_LINE_TYPES,
} from "shared/reducers/aiReducer";
import { AuthState } from "shared/reducers/authReducer";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";

import { AppDispatch } from "store/store";

type AnnotationModalProps = {
  slideDetailObject: SlideDetailState;
  authObject: AuthState;
};

const AnnotationModal = ({ slideDetailObject }: AnnotationModalProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const editedAnnotationLabelRef = useRef<HTMLTextAreaElement | null>(null);
  const editedAnnotationCountRef = useRef<HTMLInputElement | null>(null);

  const editedAnnotationLesionLineRef = useRef<HTMLSelectElement | null>(null);
  const editedAnnotationSubtypeRef = useRef<HTMLSelectElement | null>(null);

  const { editedAnnotation } = slideDetailObject;

  const [lesionTypeState, setLesionTypeState] = useState("");

  const subHierarchy = LESION_LINE_TYPE_HIERARCHY[lesionTypeState];

  return (
    <Modal
      isOpen={editedAnnotation !== undefined}
      key={editedAnnotation?.id}
      onRequestClose={() => {
        dispatch(setEditedAnnotation(undefined));
      }}
      contentLabel="Edit Annotation"
      className="Modal"
      overlayClassName="Overlay"
      onAfterOpen={() => {
        if (
          editedAnnotationLabelRef.current &&
          editedAnnotation?.type === AnnotationType.TEXT
        ) {
          const textAnn = editedAnnotation as TextAnnotation;
          editedAnnotationLabelRef.current.focus();
          editedAnnotationLabelRef.current.value = textAnn.label || "";
        }
        if (
          editedAnnotationCountRef.current &&
          editedAnnotation?.type === AnnotationType.AREA_COUNT
        ) {
          const areaCountAnn = editedAnnotation as AreaCountAnnotation;
          editedAnnotationCountRef.current.focus();
          editedAnnotationCountRef.current.value = areaCountAnn.label || "0";
        }
        if (
          editedAnnotationLesionLineRef.current &&
          editedAnnotation?.type === AnnotationType.REGION_OF_INTEREST
        ) {
          const roiAnn = editedAnnotation as RegionOfInterestAnnotation;
          const val = roiAnn.lesionLineType || "INC";
          editedAnnotationLesionLineRef.current.focus();
          editedAnnotationLesionLineRef.current.value = val;
          setLesionTypeState(val);
        }
        if (
          editedAnnotationSubtypeRef.current &&
          editedAnnotation?.type === AnnotationType.REGION_OF_INTEREST
        ) {
          const roiAnn = editedAnnotation as RegionOfInterestAnnotation;
          editedAnnotationSubtypeRef.current.value = roiAnn.subtype || "";
        }
      }}
    >
      <div className="AnnotationEditBox" key={`${editedAnnotation?.id}`}>
        <h6>
          {editedAnnotation?.type === AnnotationType.MEASUREMENT &&
            "MEASUREMENT"}
          {editedAnnotation?.type === AnnotationType.PEN_DRAWING &&
            "PEN DRAWING"}
          {editedAnnotation?.type === AnnotationType.TEXT && "TEXT"}
          {editedAnnotation?.type === AnnotationType.AREA_COUNT && "AREA COUNT"}
          {editedAnnotation?.type === AnnotationType.REGION_OF_INTEREST &&
            "REGION OF INTEREST"}
        </h6>
        {editedAnnotation?.type === AnnotationType.TEXT ? (
          <div className="AnnotationEditBox_TextArea">
            <label>Label:</label>
            <textarea rows={5} cols={40} ref={editedAnnotationLabelRef} />
          </div>
        ) : editedAnnotation?.type === AnnotationType.AREA_COUNT ? (
          <div className="AnnotationEditBox_Name">
            <label>Count:</label>
            <input
              type="number"
              ref={editedAnnotationCountRef}
              spellCheck="false"
              min={0}
            />
          </div>
        ) : editedAnnotation?.type === AnnotationType.REGION_OF_INTEREST ? (
          <div>
            <div className="AnnotationEditBox_ROI">
              <label>Lesion Type</label>
              <select
                name="lesion_line_type"
                id="lesion_line_type"
                ref={editedAnnotationLesionLineRef}
                onChange={(e) => {
                  setLesionTypeState(e.target.value || "");
                }}
              >
                {LESION_LINE_TYPES.map((e) => (
                  <option
                    key={e}
                    value={e}
                  >{`${e}: ${LESION_LINE_NAMES[e]}`}</option>
                ))}
              </select>
            </div>

            {subHierarchy && Object.keys(subHierarchy).length > 0 && (
              <div className="AnnotationEditBox_ROI" key={lesionTypeState}>
                <label>Subtype</label>
                <select
                  name="subtype"
                  id="subtype"
                  ref={editedAnnotationSubtypeRef}
                >
                  {Object.entries(subHierarchy).map(([code, name]) => (
                    <option
                      key={code}
                      value={code}
                    >{`${code}: ${name}`}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        ) : (
          <></>
        )}
        <div className="AnnotationEditBox_Buttons">
          <button
            type="submit"
            onClick={(_event) => {
              if (slideDetailObject.id && editedAnnotation) {
                dispatch(
                  commitEditedAnnotation(
                    slideDetailObject.id,
                    editedAnnotation,
                    editedAnnotationLabelRef.current?.value ||
                      editedAnnotationCountRef.current?.value ||
                      null,
                    editedAnnotationLesionLineRef.current?.value || null,
                    editedAnnotationSubtypeRef.current?.value || null
                  )
                );
              }
            }}
          >
            OK
          </button>
          <button
            type="submit"
            onClick={(_event) => {
              dispatch(setEditedAnnotation(undefined));
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AnnotationModal;
